import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, UtensilsCrossed, Flower2, Lock, ShoppingBag } from "lucide-react";
import { api, formatApiError, fmtDate, fmtUSD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n/LanguageContext";
import { SERVICES, CHEF_EXPERIENCE_TIMES } from "@/lib/services";
import Modal from "@/components/Modal";
import { Label, Input, Textarea, PrimaryButton, GhostButton } from "@/components/fields";

const EMPTY = { option: "", experience: "", date: "", name: "", email: "", phone: "", notes: "" };

export default function ServiceRequestModal({ serviceId, open, onClose }) {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const { addItem } = useCart();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [prices, setPrices] = useState({});
  const [paying, setPaying] = useState(false);

  const service = SERVICES.find((s) => s.id === serviceId) || null;
  const isChef = serviceId === "chef";

  useEffect(() => {
    if (open) {
      setDone(null);
      setForm({ ...EMPTY, name: user?.name || "", email: user?.email || "" });
      api.get("/services/pricing").then((r) => setPrices(r.data.prices || {})).catch(() => {});
    }
  }, [open, user, serviceId]);

  if (!service) return null;

  const priceFor = (optionId) => prices[`${serviceId}_${optionId}`] || null;

  const optionLabel = isChef
    ? form.option ? t(`services.chef.gatherings.${form.option}`) : ""
    : form.option ? t(`services.massage.durations.${form.option}`) : "";
  const experienceLabel = isChef && form.experience ? t(`services.chef.experiences.${form.experience}`) : "";
  const guests = isChef ? (service.gatherings.find((g) => g.id === form.option)?.guests || 2) : 2;

  const pill = (active) =>
    `rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
      active ? "bg-terracotta text-sand border-terracotta" : "border-ink/15 hover:border-terracotta text-ink"
    }`;

  const submit = async () => {
    if (!form.option) return toast.error(isChef ? t("services.chef.gatheringLbl") : t("services.massage.durationLbl"));
    if (isChef && !form.experience) return toast.error(t("services.chef.experienceLbl"));
    if (!form.date) return toast.error(t("services.modal.date"));
    if (!form.name.trim() || !form.email.trim()) return toast.error(t("services.modal.name"));
    setSending(true);
    try {
      const { data } = await api.post("/services/request", {
        service: serviceId,
        option: optionLabel,
        option_id: form.option,
        experience: experienceLabel,
        date: form.date,
        time: isChef ? CHEF_EXPERIENCE_TIMES[form.experience] || "" : "",
        guests,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: form.notes,
        lang,
      });
      setDone(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const serviceName = t(`services.${serviceId}.name`);

  const addToCart = () => {
    if (!form.option) return toast.error(isChef ? t("services.chef.gatheringLbl") : t("services.massage.durationLbl"));
    if (isChef && !form.experience) return toast.error(t("services.chef.experienceLbl"));
    if (!form.date) return toast.error(t("services.modal.date"));
    addItem({
      kind: "service",
      service: serviceId,
      label: `${serviceName} — ${optionLabel}`,
      option: optionLabel,
      option_id: form.option,
      experience: experienceLabel,
      date: form.date,
      time: isChef ? CHEF_EXPERIENCE_TIMES[form.experience] || "" : "",
      guests,
      notes: form.notes,
      lang,
    });
    toast.success(t("cart.added"));
    onClose();
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/service-deposit", {
        request_id: done.id,
        origin_url: window.location.origin,
        pay_full: true,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPaying(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={done ? t("services.modal.doneTitle") : t("services.modal.title")} testid="service-request-modal" wide>
      {done ? (
        <div className="space-y-5" data-testid="service-request-confirmation">
          <div className="rounded-3xl border-2 border-dashed border-terracotta/40 bg-white p-6 sm:p-8 space-y-4">
            <span className="flex items-center gap-2 text-terracotta font-semibold text-sm">
              {isChef ? <UtensilsCrossed size={17} /> : <Flower2 size={17} />} {serviceName}
            </span>
            <div className="text-sm space-y-2">
              <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-ink/40">{t("services.modal.summary")}</p>
              <p className="font-medium" data-testid="service-confirmation-details">
                {done.option}{done.experience ? ` · ${done.experience}` : ""}
              </p>
              <p className="text-ink/60">{fmtDate(done.date)}{done.time ? ` · ${done.time}` : ""} · {done.guests} {t("services.guestsSuffix")}</p>
              {done.total_price ? (
                <p className="font-semibold text-terracotta text-lg" data-testid="service-confirmation-total">
                  {t("services.modal.total")}: ${fmtUSD(done.total_price)}
                </p>
              ) : null}
              <p className="text-xs font-mono uppercase tracking-[0.15em] text-ink/40 pt-1">Ref {done.id.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>
          <p className="text-sm text-ink/60 text-center">{t("services.modal.doneCopy")}</p>
          {done.total_price ? (
            <div className="space-y-3" data-testid="service-payment-options">
              <div className="flex justify-center">
                <PrimaryButton data-testid="service-pay-full-btn" onClick={() => pay()} disabled={paying}>
                  <Lock size={15} /> {t("services.modal.payFull", { amount: fmtUSD(done.total_price) })}
                </PrimaryButton>
              </div>
              <p className="text-xs text-ink/45 text-center">{t("services.modal.orWait")}</p>
            </div>
          ) : null}
          <div className="flex justify-center">
            <GhostButton data-testid="service-request-done-btn" onClick={onClose}>{t("services.modal.close")}</GhostButton>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center">
              {isChef ? <UtensilsCrossed size={22} /> : <Flower2 size={22} />}
            </span>
            <div>
              <h4 className="font-serif text-xl text-ink leading-tight">{serviceName}</h4>
              <p className="text-xs text-ink/50">{t("services.priceNote")}</p>
            </div>
          </div>

          {isChef ? (
            <>
              <div>
                <Label>{t("services.chef.gatheringLbl")}</Label>
                <div className="flex flex-wrap gap-2">
                  {service.gatherings.map((g) => (
                    <button key={g.id} type="button" data-testid={`service-gathering-${g.id}`} onClick={() => setForm({ ...form, option: g.id })} className={pill(form.option === g.id)}>
                      {t(`services.chef.gatherings.${g.id}`)} · {g.guests}{priceFor(g.id) ? ` — $${priceFor(g.id)}` : ""}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>{t("services.chef.experienceLbl")}</Label>
                <div className="flex flex-wrap gap-2">
                  {service.experiences.map((x) => (
                    <button key={x} type="button" data-testid={`service-experience-${x}`} onClick={() => setForm({ ...form, experience: x })} className={pill(form.experience === x)}>
                      {t(`services.chef.experiences.${x}`)}{CHEF_EXPERIENCE_TIMES[x] ? ` · ${CHEF_EXPERIENCE_TIMES[x]}` : ""}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div>
              <Label>{t("services.massage.durationLbl")}</Label>
              <div className="flex flex-wrap gap-2">
                {service.durations.map((d) => (
                  <button key={d} type="button" data-testid={`service-duration-${d}`} onClick={() => setForm({ ...form, option: d })} className={pill(form.option === d)}>
                    {t(`services.massage.durations.${d}`)}{priceFor(d) ? ` — $${priceFor(d)}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="sv-date">{t("services.modal.date")}</Label>
              <Input id="sv-date" data-testid="service-date-input" type="date" value={form.date} onChange={set("date")} />
            </div>
            <div>
              <Label htmlFor="sv-phone">{t("services.modal.phone")}</Label>
              <Input id="sv-phone" data-testid="service-phone-input" value={form.phone} onChange={set("phone")} placeholder="+1 ..." />
            </div>
            <div>
              <Label htmlFor="sv-name">{t("services.modal.name")}</Label>
              <Input id="sv-name" data-testid="service-name-input" value={form.name} onChange={set("name")} placeholder={t("services.modal.namePh")} />
            </div>
            <div>
              <Label htmlFor="sv-email">{t("services.modal.email")}</Label>
              <Input id="sv-email" data-testid="service-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
            </div>
          </div>
          <div>
            <Label htmlFor="sv-notes">{t("services.modal.notes")}</Label>
            <Textarea id="sv-notes" data-testid="service-notes-input" value={form.notes} onChange={set("notes")} placeholder={t("services.modal.notesPh")} />
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-1">
            <GhostButton data-testid="service-add-cart-btn" onClick={addToCart}>
              <ShoppingBag size={15} /> {t("cart.addBtn")}
            </GhostButton>
            <PrimaryButton data-testid="service-submit-btn" onClick={submit} disabled={sending}>
              {sending ? t("services.modal.sending") : t("services.modal.submit")} <Check size={15} />
            </PrimaryButton>
          </div>
        </div>
      )}
    </Modal>
  );
}
